import { useCallback, useEffect, useRef } from 'react'

interface UseKeyboardNavOptions {
  onEscape?: () => void
  onEnter?: () => void
  onArrowDown?: () => void
  onArrowUp?: () => void
  onArrowLeft?: () => void
  onArrowRight?: () => void
  onTab?: () => void
  onShiftTab?: () => void
  enabled?: boolean
}

export const useKeyboardNav = (options: UseKeyboardNavOptions = {}) => {
  const {
    onEscape,
    onEnter,
    onArrowDown,
    onArrowUp,
    onArrowLeft,
    onArrowRight,
    onTab,
    onShiftTab,
    enabled = true
  } = options

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!enabled) {
        return
      }

      switch (event.key) {
        case 'Escape':
          event.preventDefault()
          onEscape?.()
          break
        case 'Enter':
          if (onEnter) {
            event.preventDefault()
            onEnter()
          }
          break
        case 'ArrowDown':
          if (onArrowDown) {
            event.preventDefault()
            onArrowDown()
          }
          break
        case 'ArrowUp':
          if (onArrowUp) {
            event.preventDefault()
            onArrowUp()
          }
          break
        case 'ArrowLeft':
          if (onArrowLeft) {
            event.preventDefault()
            onArrowLeft()
          }
          break
        case 'ArrowRight':
          if (onArrowRight) {
            event.preventDefault()
            onArrowRight()
          }
          break
        case 'Tab':
          if (event.shiftKey && onShiftTab) {
            event.preventDefault()
            onShiftTab()
          } else if (!event.shiftKey && onTab) {
            event.preventDefault()
            onTab()
          }
          break
      }
    },
    [
      enabled,
      onEscape,
      onEnter,
      onArrowDown,
      onArrowUp,
      onArrowLeft,
      onArrowRight,
      onTab,
      onShiftTab
    ]
  )

  useEffect(() => {
    if (enabled) {
      document.addEventListener('keydown', handleKeyDown)
      return () => document.removeEventListener('keydown', handleKeyDown)
    }
  }, [handleKeyDown, enabled])
}

export const useFocusTrap = (
  containerRef: React.RefObject<HTMLElement>,
  isActive: boolean = true
) => {
  const firstFocusableRef = useRef<HTMLElement | null>(null)
  const lastFocusableRef = useRef<HTMLElement | null>(null)

  const getFocusableElements = useCallback(
    (container: HTMLElement): HTMLElement[] => {
      const focusableSelectors = [
        'a[href]',
        'button:not([disabled])',
        'input:not([disabled])',
        'textarea:not([disabled])',
        'select:not([disabled])',
        '[tabindex]:not([tabindex="-1"])',
        '[role="button"]:not([disabled])',
        '[role="menuitem"]:not([disabled])'
      ]

      return Array.from(
        container.querySelectorAll(focusableSelectors.join(', '))
      ) as HTMLElement[]
    },
    []
  )

  const trapFocus = useCallback(
    (event: KeyboardEvent) => {
      if (!isActive || !containerRef.current) {
        return
      }

      if (event.key === 'Tab') {
        const focusableElements = getFocusableElements(containerRef.current)
        if (focusableElements.length === 0) {
          return
        }

        const firstElement = focusableElements[0]
        const lastElement = focusableElements[focusableElements.length - 1]

        if (event.shiftKey) {
          if (document.activeElement === firstElement) {
            event.preventDefault()
            lastElement.focus()
          }
        } else {
          if (document.activeElement === lastElement) {
            event.preventDefault()
            firstElement.focus()
          }
        }
      }
    },
    [isActive, containerRef, getFocusableElements]
  )

  useEffect(() => {
    if (isActive && containerRef.current) {
      const focusableElements = getFocusableElements(containerRef.current)
      if (focusableElements.length > 0) {
        firstFocusableRef.current = focusableElements[0]
        lastFocusableRef.current =
          focusableElements[focusableElements.length - 1]
        firstFocusableRef.current.focus()
      }

      document.addEventListener('keydown', trapFocus)
      return () => document.removeEventListener('keydown', trapFocus)
    }
  }, [isActive, trapFocus, containerRef, getFocusableElements])

  return { firstFocusableRef, lastFocusableRef }
}

export const useMenuNavigation = (
  items: HTMLElement[] | null,
  isOpen: boolean,
  onClose?: () => void,
  onNavigateLeft?: () => void,
  onNavigateRight?: () => void
) => {
  const currentIndex = useRef<number>(-1)

  const focusItem = useCallback(
    (index: number) => {
      if (!items || items.length === 0) {
        return
      }

      const validIndex = Math.max(0, Math.min(index, items.length - 1))
      currentIndex.current = validIndex
      const targetItem = items[validIndex]

      if (targetItem) {
        targetItem.focus()
        // Ensure the focused item is visible in the viewport
        targetItem.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest'
        })
      }
    },
    [items]
  )

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!isOpen || !items || items.length === 0) {
        return
      }

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault()
          focusItem(currentIndex.current + 1)
          break
        case 'ArrowUp':
          event.preventDefault()
          focusItem(currentIndex.current - 1)
          break
        case 'Home':
          event.preventDefault()
          focusItem(0)
          break
        case 'End':
          event.preventDefault()
          focusItem(items.length - 1)
          break
        case 'ArrowLeft':
          if (onNavigateLeft) {
            event.preventDefault()
            onNavigateLeft()
          }
          break
        case 'ArrowRight':
          if (onNavigateRight) {
            event.preventDefault()
            onNavigateRight()
          }
          break
        case 'Escape':
          event.preventDefault()
          onClose?.()
          break
      }
    },
    [isOpen, items, focusItem, onClose, onNavigateLeft, onNavigateRight]
  )

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown)
      return () => document.removeEventListener('keydown', handleKeyDown)
    }
  }, [handleKeyDown, isOpen])

  useEffect(() => {
    if (isOpen && items && items.length > 0) {
      currentIndex.current = 0
      const firstItem = items[0]
      if (firstItem) {
        firstItem.focus()
        firstItem.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest'
        })
      }
    } else {
      currentIndex.current = -1
    }
  }, [isOpen, items])

  // eslint-disable-next-line
  return { focusItem, currentIndex: currentIndex.current }
}
